import { Store, Share2, Crown, User } from 'lucide-react';
import { toast } from 'sonner';
import { PriceDisplay } from '../PriceDisplay';
import PriceOrContact from '../PriceOrContact';
import PhoneNumberLockup from '../PhoneNumberLockup';
import { getDisplayNumber } from '../../utils/digitMask';
import { cardFrame, riseDelay, GOLD_BADGE } from './frames';
import { useOperatorView } from '../../hooks/usePhoneOperators';
import OperatorLogo from '../OperatorLogo';

/**
 * LargeNumberCard — Cyber-Luxe phone-number card for /mobile-numbers and store pages.
 * Operator strip → branded number lockup (masked digits in gold) → store → price/share.
 */
export default function LargeNumberCard({ item, isDarkMode, isAr, onClick, index = 0 }) {
  const op = useOperatorView(item);
  const opName = isAr ? op.nameAr : op.nameEn;
  const shown = getDisplayNumber(item);
  const storeName = item?.store?.name || item?.store?.nameEn || item?.store_name || '';
  const isPersonal = item?.is_personal || item?.seller_type === 'personal';
  const negotiable = item?.negotiable === true || item?.negotiable === 'true';

  const handleShare = (e) => {
    e.stopPropagation();
    const url = window.location.origin + `/phone/${item.id}`;
    if (navigator.share) {
      navigator.share({ title: `${shown} — CloudGM`, url });
    } else {
      navigator.clipboard.writeText(url);
      toast.success(isAr ? 'تم نسخ الرابط' : 'Link copied');
    }
  };

  return (
    <div
      onClick={onClick}
      className={`group relative cursor-pointer rounded-3xl overflow-hidden transition-all duration-300 hover:-translate-y-1 cgm-shimmer cgm-rise ${cardFrame(isDarkMode)}`}
      style={riseDelay(index)}
      data-testid={`large-number-${item.id}`}
    >
      <div className="absolute inset-x-0 top-0 h-[2px]" style={{ background: `linear-gradient(90deg, ${op.accent}, #F5A962)` }} />

      <div className="p-4 sm:p-5">
        <div className="flex items-center justify-between gap-2 mb-4" dir="ltr">
          <div className="flex items-center gap-1.5 sm:gap-2 rounded-full px-2.5 py-1 sm:px-3 sm:py-1.5 min-w-0" style={{ background: op.tint }} data-testid={`large-number-operator-${item.id}`}>
            <OperatorLogo src={op.logo} name={opName} accent={op.accent} className="h-5 w-5 sm:h-6 sm:w-6" letterClass="text-[10px]" />
            <span className="text-[11px] sm:text-xs font-bold tracking-wide truncate" style={{ color: op.accent }}>{opName}</span>
          </div>
          <div className="flex items-center gap-1">
            {item.featured && (
              <span className={GOLD_BADGE} data-testid={`featured-badge-${item.id}`}>
                <Crown className="h-3 w-3" />
                {isAr ? 'مميز' : 'VIP'}
              </span>
            )}
            {isPersonal && (
              <span className="inline-flex items-center gap-0.5 text-[10px] font-semibold px-2 py-0.5 rounded-full bg-[#F5A962]/15 text-[#F59E0B] border border-[#F5A962]/30" data-testid={`personal-badge-${item.id}`}>
                <User className="h-2.5 w-2.5" />
                {isAr ? 'شخصي' : 'Personal'}
              </span>
            )}
            {negotiable && !isPersonal && (
              <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-emerald-500/15 text-emerald-500 border border-emerald-500/25">
                {isAr ? 'قابل' : 'Nego'}
              </span>
            )}
          </div>
        </div>

        <div className={`py-3.5 sm:py-4 mb-3.5 rounded-2xl border overflow-hidden whitespace-nowrap ${isDarkMode ? 'cgm-stage-dark border-white/5' : 'cgm-stage-light border-slate-100'}`} dir="ltr">
          <PhoneNumberLockup value={shown || '—'} size="card" className="w-full" testId={`large-number-value-${item.id}`} />
        </div>

        {storeName && (
          <div className={`flex items-center gap-1 mb-2.5 text-[11px] sm:text-xs ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>
            <Store className="h-3.5 w-3.5 shrink-0 text-[#1E9BD9]" />
            <span className="truncate">{storeName}</span>
          </div>
        )}

        <div className={`flex items-center justify-between gap-1 pt-3 border-t ${isDarkMode ? 'border-white/5' : 'border-slate-100'}`}>
          {item.priceOnRequest ? (
            <PriceOrContact item={{ ...item, store: item.store || {} }} variant="card" itemLabel={shown} />
          ) : (
            <span className="text-base sm:text-xl font-black truncate tabular-nums" style={{ color: op.accent }}>
              <PriceDisplay price={item.price} />
            </span>
          )}
          <button
            onClick={handleShare}
            className={`p-2 rounded-full transition-colors shrink-0 ${isDarkMode ? 'text-slate-500 hover:bg-[#1E9BD9]/15 hover:text-[#1E9BD9]' : 'text-slate-400 hover:bg-[#1E9BD9]/10 hover:text-[#1E9BD9]'}`}
            data-testid={`large-number-share-${item.id}`}
            aria-label={isAr ? 'مشاركة' : 'Share'}
          >
            <Share2 className="h-4 w-4" />
          </button>
        </div>
      </div>
    </div>
  );
}
